import type {
  CheckoutPerParticipantService,
  CheckoutServiceOption,
  ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'

import type { APIBookingDraft, APIBookingDraftRoom } from '@/scopes/checkout/infrastructure/api/types'
import { isNil, values } from 'ramda'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceOptionMapper?: ServiceOptionMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceType: ServiceType
}

export const createPerParticipantServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutPerParticipantService> = (dependencies: Dependencies) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft, genericServiceAName?: string) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName || aName === genericServiceAName)

    if (!service)
      return null

    const { aName, options } = service
    const participants = serviceParticipantsMapper.map(service, bookingDraft)
    const anyParticipantHasSelectedValue = values(participants).some(({ selectedValue }) => selectedValue)

    return {
      id: aName,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      type: serviceType,
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      options: options.reduce(
        (acc2: Record<string, CheckoutServiceOption>, option) => ({
          ...acc2,
          [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
        }),
        {},
      ),
      banners: serviceBannersMapper.map(service),
    }
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
    }
  }

  function mapSelection(service: CheckoutPerParticipantService) {
    const { participants } = service

    return Object.values(participants ?? {}).map(({ id, selectedValue }) => {
      return { participantId: id, value: selectedValue ?? null }
    })
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutPerParticipantService, step: number): ImpressionViewAncillariesItem[] {
    const selectedItems = values(service.participants ?? {}).reduce((items: SelectedItemInput[], participant) => {
      if (!participant.selectedValue)
        return items

      const option = service.options[participant.selectedValue]
      if (!option)
        return items

      items.push({
        label: option.label,
        value: option.value,
        price: option.price,
        rawPrice: option.rawPrice,
        badge: option.badge,
      })

      return items
    }, [])

    return mapDefaultSelectedItemToGA4(selectedItems, {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
